<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import UserAvatar from "@/shared/ui/user-avatar/UserAvatar.vue";
import ProfileViewTabs from "./ProfileViewTabs.vue";

const { t } = useI18n();

const props = defineProps({
  data: { type: Object, required: true },
  tab: { type: String, required: true },
  editMode: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  deleting: { type: Boolean, default: false },
  organizations: { type: Array, default: () => [] },
  canEdit: { type: Boolean, default: false },
  canEditAdmin: { type: Boolean, default: false },
  canDelete: { type: Boolean, default: false },
  showViewSwitcher: { type: Boolean, default: false },
});

defineEmits(["set-tab", "start-edit", "cancel", "delete-user", "switch-view"]);

const TABS = [
  { id: "personal", label: "Основные", svg: "huge-user" },
  { id: "access", label: "Доступ и роли", svg: "sli-system" },
  { id: "system", label: "Системная", svg: "sli-medal", adminOnly: true },
];

const visibleTabs = computed(() =>
  TABS.filter((t) => !t.adminOnly || props.canEditAdmin)
);

const fullName = computed(
  () =>
    [props.data.last_name, props.data.first_name].filter(Boolean).join(" ") ||
    "—"
);

const subtitle = computed(() => {
  const roles = Array.isArray(props.data.roles) ? props.data.roles : [];
  const roleName = roles[0]?.name;
  return (
    roleName || props.data.identifier || props.data.email || t("Пользователь")
  );
});
</script>

<template>
  <aside
    class="w-[290px] shrink-0 bg-card border-r border-border pb-5 flex flex-col gap-2 overflow-y-auto"
  >
    <div class="relative overflow-hidden bg-card">
      <div class="absolute top-3 right-3 z-10 flex items-center gap-2">
        <button
          v-if="canDelete && !editMode"
          type="button"
          class="w-9 h-9 rounded-full bg-card border border-border text-muted-foreground hover:border-destructive/40 hover:text-destructive flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="deleting"
          :title="$t('Удалить пользователя')"
          @click="$emit('delete-user')"
        >
          <SvgIcon name="huge-danger" style="fill: none" class="w-3.5 h-3.5" />
        </button>
        <button
          v-if="!editMode"
          type="button"
          class="w-9 h-9 rounded-full bg-card border border-border text-foreground flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canEdit"
          :title="!canEdit ? $t('Недостаточно прав') : ''"
          @click="$emit('start-edit')"
        >
          <SvgIcon name="huge-edit" style="fill: none" class="w-3.5 h-3.5" />
        </button>
        <button
          v-else
          type="button"
          class="w-9 h-9 rounded-full bg-card border border-border text-foreground flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="saving"
          @click="$emit('cancel')"
        >
          <SvgIcon name="huge-close" style="fill: none" class="w-3.5 h-3.5" />
        </button>
      </div>

      <div class="h-24 bg-muted border-0 border-b border-border" />

      <div class="px-4 pt-0 pb-2 -mt-10">
        <UserAvatar
          :identifier="data.identifier"
          :seed="data.uuid"
          :name="`${data.last_name || ''} ${data.first_name || ''}`.trim()"
          :src="data.photo"
          :status="data.is_active ? 'bg-green-500' : null"
          :size="64"
          class="mb-3 ring-4 ring-card"
        />

        <div class="text-foreground break-words">
          {{ fullName }}
        </div>
        <div class="text-sm text-muted-foreground mt-0.5 break-words">
          {{ subtitle }}
        </div>
      </div>
    </div>

    <nav class="flex flex-col py-2">
      <button
        v-for="t in visibleTabs"
        :key="t.id"
        type="button"
        class="nav-link"
        :class="{ 'is-active': tab === t.id }"
        @click="$emit('set-tab', t.id)"
      >
        <div
          v-if="tab === t.id"
          class="absolute left-0 top-0 bottom-0 w-[3px] bg-foreground"
        />
        <SvgIcon :name="t.svg" style="width: 16px; height: 16px; fill: none" />
        <div>{{ $t(t.label) }}</div>
      </button>
    </nav>

    <div v-if="showViewSwitcher" class="mt-auto px-4 pt-2 flex flex-col gap-2">
      <ProfileViewTabs current="user" @switch="$emit('switch-view', $event)" />
    </div>
  </aside>
</template>

<style lang="scss" scoped>
.nav-link {
  @apply relative flex items-center gap-3.5 px-5 py-2 text-left text-sm transition-colors;
  color: hsl(var(--sidebar-foreground));

  &:hover {
    @apply bg-muted;
    color: hsl(var(--sidebar-accent-foreground));

    svg {
      color: hsl(var(--sidebar-accent-foreground));
    }
  }

  &.is-active {
    @apply bg-muted font-medium;
  }
}
</style>
